import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Trash2, X, Loader2, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { author: "", rating: 5, text: "", published: true };

export default function AdminReviews() {
  const [reviews, setReviews] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.author.trim()) { toast.error("Le nom de l'auteur est requis"); return; }
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/reviews/${form.id}`, form);
      else await api.post("/admin/reviews", form);
      toast.success("Avis enregistré");
      setForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await api.delete(`/admin/reviews/${r.id}`);
    toast.success("Avis supprimé");
    load();
  };

  const toggle = async (r) => {
    await api.put(`/admin/reviews/${r.id}`, { author: r.author, rating: r.rating, text: r.text, published: !r.published });
    toast.success(r.published ? "Avis masqué" : "Avis publié");
    load();
  };

  if (form) {
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    return (
      <div className="mx-auto max-w-xl" data-testid="review-editor">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{form.id ? "Modifier l'avis" : "Nouvel avis"}</h1>
          <button data-testid="review-editor-close" onClick={() => setForm(null)} className="rounded-full bg-slate-100 p-2.5 text-slate-500"><X className="h-5 w-5" /></button>
        </div>
        <div className="card-oxy mt-5 space-y-4 p-5">
          <div>
            <label className="label-oxy">Nom du client</label>
            <input data-testid="review-author" value={form.author} onChange={(e) => set("author", e.target.value)} className="input-oxy" placeholder="Ex : Karim B." />
          </div>
          <div>
            <label className="label-oxy">Note</label>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => set("rating", n)}>
                  <Star className={`h-8 w-8 ${n <= form.rating ? "fill-gold text-gold" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-oxy">Texte de l'avis</label>
            <textarea data-testid="review-text" rows={4} value={form.text} onChange={(e) => set("text", e.target.value)} className="input-oxy resize-none"
              placeholder="Collez ici le texte d'un avis Google…" />
          </div>
          <label className="flex items-center gap-3 rounded-xl bg-slate-50 p-3.5">
            <input type="checkbox" data-testid="review-published" checked={form.published} onChange={(e) => set("published", e.target.checked)}
              className="h-5 w-5 accent-[#D4AF37]" />
            <span className="text-sm font-semibold text-slate-700">Publié sur le site</span>
          </label>
          <button data-testid="review-save-btn" onClick={save} disabled={saving} className="btn-gold w-full disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enregistrer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Avis clients</h1>
          <p className="mt-1 text-xs text-slate-500">Saisissez ici vos avis Google. Le lien vers votre fiche Google se règle dans Réglages.</p>
        </div>
        <button data-testid="review-create-btn" onClick={() => setForm({ ...EMPTY })} className="btn-gold !px-5 !py-2.5 text-sm">
          <Plus className="h-4 w-4" /> Ajouter un avis
        </button>
      </div>

      {reviews === null ? (
        <div className="mt-5 space-y-3">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : reviews.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="reviews-empty">
          <p className="font-display font-semibold text-slate-800">Aucun avis</p>
        </div>
      ) : (
        <ul className="mt-5 space-y-3" data-testid="reviews-list">
          {reviews.map((r) => (
            <li key={r.id} data-testid={`review-row-${r.id}`} className="card-oxy flex items-start justify-between gap-3 p-4 sm:p-5">
              <div className="min-w-0">
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-gold text-gold" : "text-slate-300"}`} />
                  ))}
                  {!r.published && <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">Masqué</span>}
                  {r.demo && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Exemple</span>}
                </div>
                <p className="mt-2 text-sm text-slate-700 line-clamp-2">{r.text}</p>
                <p className="mt-1.5 text-xs font-bold text-slate-900">{r.author}</p>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <button data-testid={`review-toggle-${r.id}`} onClick={() => toggle(r)} className="rounded-xl bg-slate-100 p-2.5 text-slate-500 hover:bg-gold-soft hover:text-gold-dark">
                  {r.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button data-testid={`review-edit-${r.id}`} onClick={() => setForm({ ...r })} className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-gold-soft">Modifier</button>
                <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r)} className="rounded-xl bg-slate-100 p-2.5 text-slate-500 hover:bg-red-100 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
